import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { parseCookies } from 'nookies';
import cn from 'classnames';

import Button, { Colour } from '@/components/Button';
import {
  FormCheckbox,
  FormError,
  FormInput,
  FormTextarea,
  FormSelect,
} from '@/components/form';
import LoadingSVG from '@/svg/loading.svg';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import useSendData from '@/hooks/useSendData';
import { HubSpotForm, LegalConsentOptionsMetaData } from '@/types';

type DefaultObject = { [key: string]: string };

type HubSpotFormProps = {
  buttonText?: string;
  buttonColour?: Colour;
  focusFormOnPageLoad?: boolean;
  dark?: boolean;
  form: HubSpotForm;
  params?: DefaultObject;
  formValues?: DefaultObject;
};

export default function HubSpotFormRenderer({
  form,
  params,
  buttonText = `Submit`,
  buttonColour = `purple`,
  dark = false,
  formValues,
  focusFormOnPageLoad = false,
}: HubSpotFormProps) {
  const [loading, setLoading] = useState(false);
  const { hubspotutk: hubSpotCookie } = parseCookies();
  const {
    sendData,
    state: { error: mqlError, result: mqlResult },
  } = useSendData({ endpoint: `/api/create-hubspot-lead` });

  const defaultFieldValues = form.formFieldGroups
    .flatMap((group) =>
      group.fields.filter(({ defaultValue }) => Boolean(defaultValue)),
    )
    .map(({ defaultValue, name }) => ({ [name]: defaultValue }));

  const methods = useForm({
    defaultValues: { ...defaultFieldValues },
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = methods;

  const consentFields = form.metaData.find(
    (obj) => obj.name === `legalConsentOptions`,
  );

  const parsedConsentFields: LegalConsentOptionsMetaData = consentFields
    ? JSON.parse(consentFields.value)
    : null;

  useEffect(() => {
    if (formValues) {
      const formValuesArray = Object.entries(formValues);

      if (formValuesArray.length !== 0) {
        formValuesArray.forEach((field) => {
          const [key, value] = field;
          methods.setValue(key, value);
        });
      }
    }
  }, [formValues]);

  let firstVisibleInput = ``;

  const onSubmit = async (values: DefaultObject) => {
    setLoading(true);

    const valuesWithParams = {
      ...values,
      ...params,
    };

    const entries = Object.entries(valuesWithParams);

    const [fields, userConsent] = entries.reduce(
      (formFields: any, formField) => {
        const [field, consent] = formFields;

        // Example: ['utm_content', '']
        const [key, value] = formField;

        return !key.includes(`consent`)
          ? [[...field, { name: key, value }], consent]
          : [field, [...consent, { name: key, value }]];
      },
      [[], []],
    );

    await sendData({
      consent: userConsent,
      fields,
      formId: form.guid,
      hubSpotCookie,
    });

    setLoading(false);
  };

  const successMessage = mqlResult ? mqlResult.inlineMessage : null;

  if (successMessage) return <MarkdownRenderer content={successMessage} />;

  return form.formFieldGroups.length ? (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {form.formFieldGroups.map((group, index) => {
          const formGridColumns = (cols: number) => {
            switch (cols) {
              case 1:
              default:
                return `grid-cols-1`;
              case 2:
                return `grid-cols-1 md:grid-cols-2`;
              case 3:
                return `grid-cols-1 md:grid-cols-3`;
            }
          };

          return (
            group.fields.length && (
              <div
                key={index}
                className={cn(
                  `grid gap-x-4`,
                  formGridColumns(group.fields.length),
                )}
              >
                {group.fields.map((field) => {
                  const parseFieldType = (type: string) => {
                    switch (type) {
                      case `select`:
                        return FormSelect;
                      case `textarea`:
                        return FormTextarea;
                      default:
                        return FormInput;
                    }
                  };

                  const Component = parseFieldType(field.fieldType);

                  const isFirstVisibleInput =
                    !field.hidden && !firstVisibleInput;
                  if (isFirstVisibleInput) firstVisibleInput = field.name;

                  return (
                    <Component
                      key={field.name}
                      inputClassName=""
                      className=""
                      autoFocus={!!isFirstVisibleInput && focusFormOnPageLoad} // adds autoFocus to first visible form field
                      disabled={isSubmitting}
                      hidden={field.hidden}
                      label={field.label}
                      placeholder={field.placeholder}
                      required={field.required}
                      type={field.type}
                      options={field.options}
                      defaultValue={field.defaultValue}
                      dark={dark}
                      name={field.name}
                    />
                  );
                })}
              </div>
            )
          );
        })}
        {parsedConsentFields?.communicationConsentCheckboxes?.length &&
          parsedConsentFields.communicationConsentCheckboxes.map((field) => (
            <FormCheckbox
              key={field.communicationTypeId}
              name={`consent-${field.communicationTypeId.toString()}`}
              label={field.label}
              required={field.required}
              disabled={loading}
            />
          ))}
        {mqlError && <FormError className="mb-2">{mqlError}</FormError>}

        <Button type="submit" colour={buttonColour} disabled={loading}>
          {loading && <LoadingSVG className="fill-current mr-2 w-6" />}
          {buttonText}
        </Button>
      </form>
    </FormProvider>
  ) : null;
}

HubSpotFormRenderer.Button = Button;
